"use client";

import { useEffect } from "react";
import { Trans } from "@lingui/react/macro";
import { reportClientError } from "@/lib/report-client-error";

/**
 * Route-segment error boundary: a render/data error below the root layout
 * lands here instead of white-screening. Reports through the same pipeline as
 * uncaught errors (→ Grafana "Browser errors" Slack alert) and offers a
 * retry — most render crashes are transient state, and `reset` re-renders the
 * segment without losing the rest of the app.
 */
export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError("react-render", error.message, error.stack, error.digest);
  }, [error]);

  return (
    <div className="flex-1 flex items-center justify-center px-6 py-16">
      <div className="max-w-sm text-center">
        <div className="font-serif text-xl text-foreground mb-2"><Trans>Something went wrong</Trans></div>
        <p className="text-sm text-foreground-muted leading-relaxed mb-6">
          <Trans>This part of the page hit an error. It has been reported — trying again usually helps.</Trans>
        </p>
        <button
          type="button"
          onClick={() => { reset(); }}
          className="px-4 py-2 rounded-lg text-sm font-medium transition bg-primary text-primary-foreground hover:bg-primary-hover"
        >
          <Trans>Try again</Trans>
        </button>
      </div>
    </div>
  );
}
